/**
 * Tyres and the road surface under them.
 *
 * Three permanently-running noise layers, gain-automated:
 *   roll    — a bandpass whose centre CLIMBS with speed. Louder alone reads as "closer";
 *             brighter-and-louder reads as "faster", which is the effect we want.
 *   rumble  — a low band that gives the road its weight and mass.
 *   texture — surface character: a bright hiss for wet tarmac, a modulated mid "chuff" for
 *             slush and snow. Montreal is at least half slush.
 *
 * Plus a screech: two high-Q resonant peaks on the same noise, driven by |slipAngle|. The
 * second peak is what makes it bite rather than whistle.
 *
 * Everything is one node graph built once. Surface changes crossfade a blend weight, they do
 * not rebuild anything.
 */

import { AUDIO_TUNING as T } from './tuning.js';

const Y = T.tyres;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const DEFAULT_SURFACE = Y.surface.road;

export class TyreVoice {
  constructor(graph) {
    this.graph = graph;
    this.built = false;

    this.speed = 0;
    this.slip = 0;
    this.grounded = 1;
    this.surfaceKey = 'road';
    this.weather = 'thaw';
    /** Blended surface descriptor, smoothed toward the target so changes do not click. */
    this.mix = { roll: 1, hiss: 0, chuff: 0, hp: 200, screech: 1 };
    this.enabled = true;
  }

  build() {
    const g = this.graph;
    if (!g.live || this.built) return;
    const a = g.actx;

    this.out = a.createGain();
    this.out.gain.value = 1;
    this.out.connect(g.bus.tyres);

    // ---- shared noise, one source per layer so playbackRate can de-correlate them
    const src = (rate, kind) => { const s = g.makeNoiseSource(kind); s.playbackRate.value = rate; return s; };

    // roll
    this.rollSrc = src(1.0, 'white');
    this.rollBp = a.createBiquadFilter();
    this.rollBp.type = 'bandpass';
    this.rollBp.frequency.value = Y.rollHzBase;
    this.rollBp.Q.value = Y.rollQ;
    this.rollHp = a.createBiquadFilter();
    this.rollHp.type = 'highpass';
    this.rollHp.frequency.value = DEFAULT_SURFACE.hp;
    this.rollGain = a.createGain(); this.rollGain.gain.value = 0.0001;
    this.rollSrc.connect(this.rollBp); this.rollBp.connect(this.rollHp);
    this.rollHp.connect(this.rollGain); this.rollGain.connect(this.out);

    // rumble
    this.rumbleSrc = src(0.79, 'pink');
    this.rumbleBp = a.createBiquadFilter();
    this.rumbleBp.type = 'bandpass';
    this.rumbleBp.frequency.value = Y.rumbleHz;
    this.rumbleBp.Q.value = Y.rumbleQ;
    this.rumbleGain = a.createGain(); this.rumbleGain.gain.value = 0.0001;
    this.rumbleSrc.connect(this.rumbleBp); this.rumbleBp.connect(this.rumbleGain);
    this.rumbleGain.connect(this.out);

    // wet hiss
    this.hissSrc = src(1.13, 'white');
    this.hissBp = a.createBiquadFilter();
    this.hissBp.type = 'highpass';
    this.hissBp.frequency.value = Y.hissHz;
    this.hissBp.Q.value = Y.hissQ;
    this.hissGain = a.createGain(); this.hissGain.gain.value = 0.0001;
    this.hissSrc.connect(this.hissBp); this.hissBp.connect(this.hissGain); this.hissGain.connect(this.out);

    // slush chuff — amplitude-modulated so it splatters
    this.chuffSrc = src(0.91, 'white');
    this.chuffBp = a.createBiquadFilter();
    this.chuffBp.type = 'bandpass';
    this.chuffBp.frequency.value = Y.chuffHz;
    this.chuffBp.Q.value = Y.chuffQ;
    this.chuffAmBase = a.createGain(); this.chuffAmBase.gain.value = 1 - Y.chuffAmDepth;
    this.chuffAm = a.createOscillator(); this.chuffAm.type = 'sine';
    this.chuffAm.frequency.value = Y.chuffAmHz;
    this.chuffAmDepth = a.createGain(); this.chuffAmDepth.gain.value = Y.chuffAmDepth;
    this.chuffAm.connect(this.chuffAmDepth);
    this.chuffAmDepth.connect(this.chuffAmBase.gain);
    this.chuffAm.start(a.currentTime);
    this.chuffGain = a.createGain(); this.chuffGain.gain.value = 0.0001;
    this.chuffSrc.connect(this.chuffBp); this.chuffBp.connect(this.chuffAmBase);
    this.chuffAmBase.connect(this.chuffGain); this.chuffGain.connect(this.out);

    // screech — two resonances in series on a dedicated source
    this.scrSrc = src(1.0, 'white');
    this.scrBp1 = a.createBiquadFilter();
    this.scrBp1.type = 'bandpass';
    this.scrBp1.frequency.value = Y.screechHz;
    this.scrBp1.Q.value = Y.screechQ;
    this.scrBp2 = a.createBiquadFilter();
    this.scrBp2.type = 'peaking';
    this.scrBp2.frequency.value = Y.screechHz2;
    this.scrBp2.Q.value = Y.screechQ2;
    this.scrBp2.gain.value = 14;
    this.scrGain = a.createGain(); this.scrGain.gain.value = 0.0001;
    this.scrSrc.connect(this.scrBp1); this.scrBp1.connect(this.scrBp2);
    this.scrBp2.connect(this.scrGain); this.scrGain.connect(this.out);

    this.built = true;
  }

  /** Fed from ctx.player in the presentation pass. */
  set(speed, slipAngle, surfaceKey, grounded, weather) {
    this.speed = speed;
    this.slip = Math.abs(slipAngle);
    if (surfaceKey && Y.surface[surfaceKey]) this.surfaceKey = surfaceKey;
    this.grounded = grounded;
    if (weather) this.weather = weather;
  }

  update(dt) {
    // ---- blend the surface descriptor (runs live or not, so stats are honest)
    const base = Y.surface[this.surfaceKey] || DEFAULT_SURFACE;
    const overlayKey = Y.weatherOverlay[this.weather];
    const overlay = overlayKey ? Y.surface[overlayKey] : null;
    const wb = overlay && overlayKey !== this.surfaceKey ? Y.weatherBlend : 0;

    const tgt = this._tgt || (this._tgt = { roll: 1, hiss: 0, chuff: 0, hp: 200, screech: 1 });
    tgt.roll = base.roll * (1 - wb) + (overlay ? overlay.roll : 0) * wb;
    tgt.hiss = base.hiss * (1 - wb) + (overlay ? overlay.hiss : 0) * wb;
    tgt.chuff = base.chuff * (1 - wb) + (overlay ? overlay.chuff : 0) * wb;
    tgt.hp = base.hp * (1 - wb) + (overlay ? overlay.hp : 0) * wb;
    tgt.screech = base.screech * (1 - wb) + (overlay ? overlay.screech : 0) * wb;

    const k = 1 - Math.exp(-Y.surfaceSmooth * dt);
    const m = this.mix;
    m.roll += (tgt.roll - m.roll) * k;
    m.hiss += (tgt.hiss - m.hiss) * k;
    m.chuff += (tgt.chuff - m.chuff) * k;
    m.hp += (tgt.hp - m.hp) * k;
    m.screech += (tgt.screech - m.screech) * k;

    const spd01 = clamp01(this.speed / Y.fullSpeed);
    const ground = this.grounded ? 1 : 0.06;
    this.roll01 = spd01;
    this.screech01 = clamp01((this.slip - Y.slipFrom) / (Y.slipFull - Y.slipFrom)) * spd01 * ground;

    if (!this.graph.live || !this.built || !this.enabled) return;

    const g = this.graph;
    const gl = Y.paramGlide;
    // Rolling noise: level scales with speed^0.8 (perceptual), band climbs linearly.
    const rollLvl = Y.rollGain * m.roll * Math.pow(spd01, 0.8) * ground;
    g.param(this.rollGain.gain, 0.0001 + rollLvl, gl);
    g.param(this.rollBp.frequency, Y.rollHzBase + Y.rollHzPerSpeed * this.speed, gl);
    g.param(this.rollHp.frequency, m.hp, gl);

    g.param(this.rumbleGain.gain, 0.0001 + Y.rumbleGain * m.roll * Math.pow(spd01, 0.6) * ground, gl);

    g.param(this.hissGain.gain, 0.0001 + Y.hissGain * m.hiss * Math.pow(spd01, 0.7) * ground, gl);

    g.param(this.chuffGain.gain, 0.0001 + Y.chuffGain * m.chuff * Math.pow(spd01, 0.55) * ground, gl);
    // Chuff modulation rate rises with speed — the wheel is throwing slush faster.
    g.param(this.chuffAm.frequency, Y.chuffAmHz * (0.5 + 1.6 * spd01), gl);

    const scr = this.screech01 * m.screech;
    g.param(this.scrGain.gain, 0.0001 + Y.screechGain * scr, 0.03);
    const scrHz = Y.screechHz + Y.screechHzPerSpeed * this.speed;
    g.param(this.scrBp1.frequency, scrHz, gl);
    g.param(this.scrBp2.frequency, scrHz * (Y.screechHz2 / Y.screechHz), gl);
  }

  setEnabled(on) {
    this.enabled = on;
    if (this.graph.live && this.built) this.graph.param(this.out.gain, on ? 1 : 0.0001, 0.05);
  }

  reset() {
    this.speed = 0; this.slip = 0; this.grounded = 1; this.surfaceKey = 'road';
    this.roll01 = 0; this.screech01 = 0;
  }

  stats() {
    return {
      speed: +this.speed.toFixed(2),
      slip: +this.slip.toFixed(3),
      surface: this.surfaceKey,
      roll01: +((this.roll01 || 0)).toFixed(3),
      screech01: +((this.screech01 || 0)).toFixed(3),
    };
  }
}
